import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type ChannelPlatformStatus, type ChannelsResponse } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Hash, Lock, UserRound } from "lucide-react";
import { useState } from "react";
import { PLATFORM_LABEL } from "./channel-mode";
import {
  bareChannelName,
  choiceFromCurrent,
  defaultChannelPlatforms,
  sameChoice,
  type DefaultChannelChoice,
} from "@/lib/default-channel";
import { saveDefaultChannelChoice } from "./save-default-channel";
import { DefaultChannelPicker, DefaultChannelSaveError } from "./DefaultChannelPicker";

/**
 * The Boo's default channel on the Channels tab, with a Change dialog using
 * the onboarding picker. Render only when the Boo reports defaultChannel
 * (null or a record); older Boos omit the key.
 */
export function DefaultChannelCard({
  containerId,
  data,
  platformStatuses,
  canToggle,
  botName,
}: {
  containerId: string;
  data: ChannelsResponse;
  platformStatuses: ChannelPlatformStatus[] | undefined;
  canToggle: boolean;
  /** The Boo's name, for the picker's /invite hint. */
  botName?: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const current = data.defaultChannel ?? null;
  const fallback = data.fallback ?? null;
  const [choice, setChoice] = useState<DefaultChannelChoice>(choiceFromCurrent(current));
  const platforms = defaultChannelPlatforms(platformStatuses);

  const save = useMutation({
    mutationFn: () =>
      saveDefaultChannelChoice(containerId, choice, current, { onCreated: setChoice }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["channels", containerId] });
      void queryClient.invalidateQueries({ queryKey: ["channel-directory", containerId] });
      setOpen(false);
    },
  });

  const noneOption = fallback
    ? { title: "Direct message", detail: fallback.displayName ?? undefined }
    : { title: "No channel" };
  const showPlatform = platforms.length > 1;
  const name = botName?.trim() || "Boo";

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">Where {name} checks in</span>
        <span className="text-sm text-muted-foreground">
          It still works in any channel you invite it to.
        </span>
      </div>
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex min-w-0 items-center gap-1.5 text-sm">
          {current ? (
            <>
              {current.surfaceType === "private_channel" ? (
                <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Private" />
              ) : (
                <Hash className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate font-medium">
                {bareChannelName(current.displayName) ?? current.channelOrSpaceId}
              </span>
              {showPlatform && (
                <span className="shrink-0 text-muted-foreground">
                  {PLATFORM_LABEL[current.platform]}
                </span>
              )}
            </>
          ) : (
            <>
              <UserRound className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {noneOption.title}
                {noneOption.detail && (
                  <span className="text-muted-foreground"> · {noneOption.detail}</span>
                )}
              </span>
            </>
          )}
        </span>
        {canToggle && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setChoice(choiceFromCurrent(current));
              save.reset();
              setOpen(true);
            }}
          >
            Change
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Where should {name} check in?</DialogTitle>
            <DialogDescription className="sr-only">
              It still works in any channel you invite it to.
            </DialogDescription>
          </DialogHeader>
          <DefaultChannelPicker
            containerId={containerId}
            platforms={platforms}
            value={choice}
            onChange={setChoice}
            noneOption={noneOption}
            botName={botName}
            disabled={save.isPending}
          />
          {save.isError && (
            <DefaultChannelSaveError
              error={save.error}
              platform={choice.kind === "none" ? undefined : choice.platform}
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={sameChoice(choice, choiceFromCurrent(current))}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
